import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import PageFrame from "./PageFrame";
import api from "../bootstrapApi";
import DirectoryRowCard from "../components/DirectoryRowCard";
import {
  saasCircleCountSx,
  saasDirectoryWrapSx,
  saasHeroActionButtonSx,
  saasInputSx,
  saasSectionCardSx,
  saasSoftCountSx,
} from "../components/saasStyles";

const initialLeadForm = {
  displayName: "",
  whatsappNumber: "",
  locationText: "",
  productId: "",
  notes: "",
};

const initialEditForm = {
  firstName: "",
  lastName: "",
  displayName: "",
  whatsappNumber: "",
  locationText: "",
  notes: "",
  contactNotes: "",
  productId: "",
  quantity: 1,
  itemNotes: "",
  status: "lead_new",
  statusNote: "",
  attributeValues: {},
};

function getRequestStatus(result) {
  if (result?.status !== "rejected") {
    return null;
  }

  return result.reason?.response?.status || null;
}

function getLeadLoadErrorText(results) {
  const statuses = results.map(getRequestStatus).filter(Boolean);

  if (statuses.includes(401)) {
    return "Deine Sitzung ist abgelaufen. Bitte melde dich neu an.";
  }

  if (statuses.includes(403)) {
    return "Du hast keine Berechtigung fuer Leads.";
  }

  return "Les prospects n'ont pas pu etre charges.";
}

function getLeadDetailErrorText(error) {
  const status = error?.response?.status;

  if (status === 401) {
    return "Deine Sitzung ist abgelaufen. Bitte melde dich neu an.";
  }

  if (status === 403) {
    return "Du hast keine Berechtigung fuer diesen Lead.";
  }

  if (status === 404) {
    return "Der Lead wurde nicht gefunden.";
  }

  return "Les details du prospect n'ont pas pu etre charges.";
}

function buildAttributeValueState(detail) {
  const next = {};
  (detail.items || []).forEach((item) => {
    next[item.id] = {};
    (item.requirements || []).forEach((requirement) => {
      next[item.id][requirement.definitionId] = requirement.value || "";
    });
  });
  return next;
}

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [products, setProducts] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [form, setForm] = useState(initialLeadForm);
  const [editForm, setEditForm] = useState(initialEditForm);
  const [message, setMessage] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [quickAction, setQuickAction] = useState("default");
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    const results = await Promise.allSettled([
      api.get("/api/leads"),
      api.get("/api/products"),
      api.get("/api/lead-statuses"),
    ]);
    const [leadsResponse, productsResponse, statusesResponse] = results;

    if (leadsResponse.status !== "fulfilled" || productsResponse.status !== "fulfilled") {
      throw new Error(getLeadLoadErrorText(results));
    }

    setLeads(leadsResponse.value.data);
    setProducts(productsResponse.value.data);
    setLeadStatuses(
      statusesResponse.status === "fulfilled"
        ? statusesResponse.value.data
        : ["lead_new", "lead_contacted", "lead_qualified", "lead_won", "lead_lost"]
    );
  };

  const loadLeadDetail = async (leadId) => {
    const response = await api.get(`/api/leads/${leadId}`);
    const detail = response.data;
    setSelectedLead(detail);
    setEditForm({
      firstName: detail.firstName || "",
      lastName: detail.lastName || "",
      displayName: detail.contactName || "",
      whatsappNumber: detail.whatsappNumber || "",
      locationText: detail.locationText || "",
      notes: detail.notes || "",
      contactNotes: detail.contactNotes || "",
      productId: detail.items[0]?.productId ? String(detail.items[0].productId) : "",
      quantity: detail.items[0]?.quantity || 1,
      itemNotes: detail.items[0]?.notes || "",
      status: detail.status || "lead_new",
      statusNote: "",
      attributeValues: buildAttributeValueState(detail),
    });
  };

  useEffect(() => {
    loadData().catch((error) => {
      setMessage({ type: "error", text: error.message || "Les prospects n'ont pas pu etre charges." });
    });
  }, []);

  useEffect(() => {
    if (!selectedLeadId) {
      setSelectedLead(null);
      setEditForm(initialEditForm);
      return;
    }

    loadLeadDetail(selectedLeadId).catch((error) => {
      setMessage({ type: "error", text: getLeadDetailErrorText(error) });
    });
  }, [selectedLeadId]);

  const filteredLeads = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const base = !needle
      ? leads
      : leads.filter((lead) => {
          const haystack = [
            lead.contactName,
            lead.whatsappNumber,
            lead.locationText,
            lead.status,
            ...lead.items.map((item) => item.productName),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return haystack.includes(needle);
        });

    if (quickAction === "new") {
      return base.filter((lead) => lead.status === "lead_new");
    }
    if (quickAction === "qualified") {
      return base.filter((lead) => lead.status === "lead_qualified");
    }
    if (quickAction === "won") {
      return base.filter((lead) => lead.status === "lead_won");
    }
    return base;
  }, [leads, search, quickAction]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      await api.post("/api/leads", {
        ...form,
        productId: Number(form.productId),
        sourceChannel: "backoffice",
      });
      setForm(initialLeadForm);
      await loadData();
      setMessage({ type: "success", text: "Lead wurde angelegt." });
    } catch (_error) {
      setMessage({ type: "error", text: "Lead konnte nicht gespeichert werden." });
    }
  };

  const handleSaveLead = async () => {
    if (!selectedLeadId) {
      return;
    }

    try {
      setSaving(true);
      const response = await api.patch(`/api/leads/${selectedLeadId}`, {
        ...editForm,
        productId: editForm.productId ? Number(editForm.productId) : null,
        quantity: Number(editForm.quantity || 1),
      });
      await loadData();
      setSelectedLead(response.data);
      setMessage({ type: "success", text: "Lead wurde aktualisiert." });
      setEditForm((current) => ({ ...current, statusNote: "" }));
    } catch (_error) {
      setMessage({ type: "error", text: "Lead konnte nicht aktualisiert werden." });
    } finally {
      setSaving(false);
    }
  };

  const handleConvertLead = async () => {
    if (!selectedLeadId) {
      return;
    }

    try {
      await api.post("/api/orders/from-lead", { leadId: Number(selectedLeadId) });
      await loadData();
      await loadLeadDetail(selectedLeadId);
      setMessage({ type: "success", text: "Lead wurde in einen Auftrag umgewandelt." });
    } catch (error) {
      const errorCode = error?.response?.data?.error;
      setMessage({
        type: "error",
        text:
          errorCode === "lead_requirements_incomplete"
            ? "Les champs obligatoires du produit doivent etre completes avant la conversion en commande."
            : "Lead konnte nicht in einen Auftrag umgewandelt werden.",
      });
    }
  };

  return (
    <PageFrame
      eyebrow="SmartDoctors"
      title="Repertoire des prospects"
      description="Vue claire des prospects, des contacts et des actions commerciales."
      stats={[]}
      action={
        <Button variant="contained" startIcon={<AddCircleRoundedIcon />} onClick={() => setCreateOpen(true)} sx={saasHeroActionButtonSx}>
          + Nouveau prospect
        </Button>
      }
    >
      {message ? <Alert severity={message.type}>{message.text}</Alert> : null}

      <Box sx={{ ...saasDirectoryWrapSx, borderRadius: "44px", px: { xs: 2, md: 2.5 }, py: { xs: 1.2, md: 1.4 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
          Afficher {filteredLeads.length} elements
        </Typography>

        <Stack direction={{ xs: "column", xl: "row" }} spacing={1.25} alignItems={{ xs: "stretch", xl: "center" }}>
          <TextField
            label="Rechercher"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{
              ...saasInputSx,
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "18px",
                minHeight: 44,
                backgroundColor: "#fff",
                boxShadow: "none",
              },
            }}
            InputProps={{
              startAdornment: <SearchRoundedIcon sx={{ color: "#5f6fff", mr: 1 }} />,
            }}
          />

          <FormControl sx={{ minWidth: { xs: "100%", xl: 350 } }}>
            <Select
              value={quickAction}
              onChange={(event) => setQuickAction(event.target.value)}
              displayEmpty
              sx={{
                minHeight: 56,
                borderRadius: "18px",
                bgcolor: "#fff",
                fontWeight: 700,
                boxShadow: "none",
              }}
            >
              <MenuItem value="default">Action rapide</MenuItem>
              <MenuItem value="new">Nouveaux prospects</MenuItem>
              <MenuItem value="qualified">Qualifies</MenuItem>
              <MenuItem value="won">Gagnes</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ ...saasSoftCountSx, minWidth: 88, minHeight: 44, bgcolor: "#f5f7ff" }}>
            <Typography variant="body2" sx={{ color: "#5867ff", fontWeight: 700 }}>
              {filteredLeads.length} leads
            </Typography>
          </Box>

          <Box
            sx={{
              width: 58,
              height: 44,
              borderRadius: "16px",
              border: "1px solid #dfe6f4",
              bgcolor: "#fff",
              display: "grid",
              placeItems: "center",
            }}
          >
            <Badge badgeContent={leads.filter((lead) => lead.status === "lead_new").length} color="error">
              <IconButton size="small" sx={{ color: "#5a68ff" }}>
                <NotificationsRoundedIcon />
              </IconButton>
            </Badge>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1.2fr 0.8fr" }, gap: 3 }}>
        <Box sx={{ ...saasSectionCardSx, borderRadius: "44px", display: "grid", gap: 2.25, px: { xs: 2, md: 2.4 }, py: { xs: 1.4, md: 1.8 } }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: 18, mb: 0.3 }}>
                Prospects
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Vue structuree avec selection rapide, coordonnees et contexte commercial.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.25}>
              <Box sx={saasCircleCountSx}>
                <Box>
                  <Typography variant="caption" sx={{ color: "#7d89a2" }}>
                    Visibles
                  </Typography>
                  <Typography variant="h6" sx={{ color: "#25324b", textAlign: "center" }}>
                    {filteredLeads.length}
                  </Typography>
                </Box>
              </Box>

              <Box sx={saasCircleCountSx}>
                <Box>
                  <Typography variant="caption" sx={{ color: "#7d89a2" }}>
                    Selection
                  </Typography>
                  <Typography variant="h6" sx={{ color: "#25324b", textAlign: "center" }}>
                    {selectedLeadId ? 1 : 0}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Box>

          {filteredLeads.map((lead) => (
            <DirectoryRowCard
              key={lead.id}
              selected={selectedLeadId === lead.id}
              avatarText={(lead.contactName || "L").slice(0, 1).toUpperCase()}
              title={lead.contactName || "Contact inconnu"}
              subtitle={`ID-${lead.id}`}
              chips={[
                { label: lead.sourceChannel === "whatsapp" ? "WhatsApp" : "Backoffice", color: "default" },
                { label: lead.status === "lead_new" ? "Nouveau" : lead.status, color: lead.status === "lead_new" ? "success" : "default" },
              ]}
              columns={[
                {
                  label: "Contact",
                  value: lead.whatsappNumber || "-",
                  secondary: `Cree le ${new Date(lead.createdAt).toLocaleDateString("fr-FR")}`,
                },
                {
                  label: "Organisation",
                  value: lead.items[0]?.categoryName || "Pipeline commerciale",
                  secondary: lead.items.map((item) => item.productName).join(", ") || "-",
                },
              ]}
              actionLabel="Choisir"
              onSelect={() => setSelectedLeadId(lead.id)}
            />
          ))}
        </Box>

        <Box sx={{ ...saasSectionCardSx, borderRadius: "44px", display: "grid", gap: 2, px: { xs: 2, md: 2.4 }, py: { xs: 1.4, md: 1.8 }, alignContent: "start" }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: 18, mb: 0.3 }}>
              Espace prospect
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestion du contact, du produit, du statut et de l'historique du prospect.
            </Typography>
          </Box>

          {!selectedLead ? (
            <Typography variant="body2" color="text.secondary">
              Choisissez un prospect a gauche pour le modifier.
            </Typography>
          ) : (
            <>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Box sx={{ ...saasSoftCountSx, minWidth: 120 }}>
                  <Typography variant="caption" sx={{ color: "#7d89a2" }}>
                    Status
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#25324b", fontWeight: 800 }}>
                    {selectedLead.status}
                  </Typography>
                </Box>
                <Box sx={{ ...saasSoftCountSx, minWidth: 120 }}>
                  <Typography variant="caption" sx={{ color: "#7d89a2" }}>
                    Source
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#25324b", fontWeight: 800 }}>
                    {selectedLead.sourceChannel}
                  </Typography>
                </Box>
                <Box sx={{ ...saasSoftCountSx, minWidth: 120 }}>
                  <Typography variant="caption" sx={{ color: "#7d89a2" }}>
                    Champs requis
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#25324b", fontWeight: 800 }}>
                    {selectedLead.missingRequiredCount || 0} manquant(s)
                  </Typography>
                </Box>
              </Stack>

              <TextField label="Prenom" value={editForm.firstName} onChange={(event) => setEditForm((current) => ({ ...current, firstName: event.target.value }))} sx={saasInputSx} />
              <TextField label="Nom" value={editForm.lastName} onChange={(event) => setEditForm((current) => ({ ...current, lastName: event.target.value }))} sx={saasInputSx} />
              <TextField label="Nom affiche" value={editForm.displayName} onChange={(event) => setEditForm((current) => ({ ...current, displayName: event.target.value }))} sx={saasInputSx} />
              <TextField label="Numero WhatsApp" value={editForm.whatsappNumber} onChange={(event) => setEditForm((current) => ({ ...current, whatsappNumber: event.target.value }))} sx={saasInputSx} />
              <TextField label="Localisation" value={editForm.locationText} onChange={(event) => setEditForm((current) => ({ ...current, locationText: event.target.value }))} sx={saasInputSx} />
              <TextField
                select
                label="Produkt"
                value={editForm.productId}
                onChange={(event) => setEditForm((current) => ({ ...current, productId: event.target.value }))}
                sx={saasInputSx}
              >
                {products.map((product) => (
                  <MenuItem key={product.id} value={product.id}>
                    {product.category_name} - {product.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField label="Menge" type="number" value={editForm.quantity} onChange={(event) => setEditForm((current) => ({ ...current, quantity: event.target.value }))} sx={saasInputSx} />
              <TextField
                select
                label="Statut du prospect"
                value={editForm.status}
                onChange={(event) => setEditForm((current) => ({ ...current, status: event.target.value }))}
                sx={saasInputSx}
              >
                {leadStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
              <TextField label="Note du prospect" multiline minRows={3} value={editForm.notes} onChange={(event) => setEditForm((current) => ({ ...current, notes: event.target.value }))} sx={saasInputSx} />
              <TextField label="Note contact" multiline minRows={2} value={editForm.contactNotes} onChange={(event) => setEditForm((current) => ({ ...current, contactNotes: event.target.value }))} sx={saasInputSx} />
              <TextField label="Note produit" multiline minRows={2} value={editForm.itemNotes} onChange={(event) => setEditForm((current) => ({ ...current, itemNotes: event.target.value }))} sx={saasInputSx} />
              <TextField label="Note de statut" multiline minRows={2} value={editForm.statusNote} onChange={(event) => setEditForm((current) => ({ ...current, statusNote: event.target.value }))} sx={saasInputSx} />

              {(selectedLead.items || []).map((item) => (
                <Box key={item.id} sx={{ border: "1px solid #e1e7f2", borderRadius: "18px", p: 1.4, display: "grid", gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: "#25324b", fontWeight: 800 }}>
                    {item.productName} | {item.categoryName || "Categorie"}
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip
                      label={item.productType === "custom" ? "Sur mesure" : "Standard"}
                      size="small"
                      sx={{ borderRadius: "999px", fontWeight: 700 }}
                    />
                    <Chip
                      label={`${item.missingRequiredCount || 0} champs requis manquants`}
                      size="small"
                      sx={{ borderRadius: "999px", fontWeight: 700 }}
                    />
                  </Stack>
                  {(item.requirements || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Aucun champ structure specifique n'est defini pour cette categorie.
                    </Typography>
                  ) : (
                    (item.requirements || []).map((requirement) =>
                      requirement.fieldType === "select" ? (
                        <TextField
                          key={requirement.definitionId}
                          select
                          label={requirement.required ? `${requirement.label} *` : requirement.label}
                          value={editForm.attributeValues?.[item.id]?.[requirement.definitionId] || ""}
                          onChange={(event) =>
                            setEditForm((current) => ({
                              ...current,
                              attributeValues: {
                                ...current.attributeValues,
                                [item.id]: {
                                  ...(current.attributeValues?.[item.id] || {}),
                                  [requirement.definitionId]: event.target.value,
                                },
                              },
                            }))
                          }
                          sx={saasInputSx}
                        >
                          <MenuItem value="">Choisir</MenuItem>
                          {(requirement.options || []).map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        <TextField
                          key={requirement.definitionId}
                          label={requirement.required ? `${requirement.label} *` : requirement.label}
                          value={editForm.attributeValues?.[item.id]?.[requirement.definitionId] || ""}
                          onChange={(event) =>
                            setEditForm((current) => ({
                              ...current,
                              attributeValues: {
                                ...current.attributeValues,
                                [item.id]: {
                                  ...(current.attributeValues?.[item.id] || {}),
                                  [requirement.definitionId]: event.target.value,
                                },
                              },
                            }))
                          }
                          sx={saasInputSx}
                        />
                      )
                    )
                  )}
                </Box>
              ))}

              <Stack direction={{ xs: "column", md: "row" }} spacing={1.25}>
                <Button variant="contained" onClick={handleSaveLead} disabled={saving} sx={{ borderRadius: "14px" }}>
                  Enregistrer le prospect
                </Button>
                <Button variant="outlined" onClick={handleConvertLead} disabled={selectedLead.status === "lead_won"} sx={{ borderRadius: "14px" }}>
                  Convertir en commande
                </Button>
              </Stack>

              <Box sx={{ display: "grid", gap: 1 }}>
                <Typography variant="subtitle2" sx={{ color: "#25324b", fontWeight: 800 }}>
                  Historique
                </Typography>
                {(selectedLead.history || []).map((entry) => (
                  <Box key={entry.id} sx={{ border: "1px solid #e1e7f2", borderRadius: "16px", p: 1.25, bgcolor: "#fdfefe" }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#25324b" }}>
                      {entry.oldStatus || "start"} -> {entry.newStatus}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                      {entry.note || "Keine Notiz"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                      {new Date(entry.createdAt).toLocaleString("fr-FR")}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Creer un prospect manuellement</DialogTitle>
        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent sx={{ display: "grid", gap: 2 }}>
            <TextField
              label="Name"
              value={form.displayName}
              onChange={(event) => setForm((current) => ({ ...current, displayName: event.target.value }))}
              sx={saasInputSx}
            />
            <TextField
              label="Numero WhatsApp"
              value={form.whatsappNumber}
              onChange={(event) => setForm((current) => ({ ...current, whatsappNumber: event.target.value }))}
              sx={saasInputSx}
            />
            <TextField
              label="Localisation"
              value={form.locationText}
              onChange={(event) => setForm((current) => ({ ...current, locationText: event.target.value }))}
              sx={saasInputSx}
            />
            <TextField
              select
              label="Produkt"
              value={form.productId}
              onChange={(event) => setForm((current) => ({ ...current, productId: event.target.value }))}
              sx={saasInputSx}
            >
              {products.map((product) => (
                <MenuItem key={product.id} value={product.id}>
                  {product.category_name} - {product.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Note"
              multiline
              minRows={3}
              value={form.notes}
              onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
              sx={saasInputSx}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button
              type="submit"
              variant="contained"
              onClick={() => {
                setCreateOpen(false);
              }}
            >
              Enregistrer le prospect
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </PageFrame>
  );
}
